import React from 'react';
import CountryFlag from './CountryFlag';
import { useIntl } from 'react-intl';
import GlobeMap from './GlobeMap';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const GeographicPresenceSection = ({ geoData }) => {
  const intl = useIntl();
  const { ref: statsRef, inView: statsInView } = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const {
    title = intl.formatMessage({ id: 'geographic.presence.title' }),
    description = intl.formatMessage({ id: 'geographic.presence.description' }),
    totalCountries = 5,
    totalEmployees = 85,
    countries: rawCountries = [],
    additionalProjects: rawAdditionalProjects = []
  } = geoData || {};

  const knownCountries = [
    'South Africa',
    'Afrique du Sud',
    'Cameroon',
    'Cameroun',
    'Kenya',
    'Nigeria',
    'Benin',
    'Bénin'
  ];

  const extractCountryNames = (name) => {
    for (const country of knownCountries) {
      if (name.includes(country)) {
        return country;
      }
    }
    return name.split(/[\n,]/)[0].trim();
  };

  const countries = rawCountries
    .filter((country, index, self) => {
      const cleanName = extractCountryNames(country.name);
      return index === self.findIndex((c) => extractCountryNames(c.name) === cleanName);
    })
    .map((country) => {
      const cleanName = extractCountryNames(country.name);

      const getFlagFromName = (name) => {
        const flags = {
          Cameroun: 'CM',
          Cameroon: 'CM',
          Kenya: 'KE',
          Nigeria: 'NG',
          Bénin: 'BJ',
          Benin: 'BJ',
          'Afrique du Sud': 'ZA',
          'South Africa': 'ZA'
        };
        return flags[name] || 'XX';
      };

      return {
        name: cleanName,
        cities: [],
        flag: country.flag || getFlagFromName(cleanName)
      };
    });

  const additionalProjects = (
    Array.isArray(rawAdditionalProjects) ? rawAdditionalProjects : []
  ).map((project) => ({
    country: project.country,
    status: project.status,
    link: typeof project.link === 'string' ? project.link.trim() : ''
  }));

  const isExternalUrl = (url) => /^https?:\/\//i.test(url);
  const normalizeInternalPath = (path) => {
    if (!path) return '/';
    if (path.startsWith('/') || path.startsWith('#') || path.startsWith('?')) return path;
    return `/${path}`;
  };

  const renderAdditionalProject = (project, index) => {
    const link = project.link;
    const clickable = Boolean(link);
    const key = `${project.country || 'project'}-${project.status || 'status'}-${index}`;
    const ariaLabel = `${project.country} — ${project.status}`;

    const className = `group flex items-center justify-between py-5 border-b border-white/10 transition-colors ${
      clickable ? 'cursor-pointer hover:border-[#16A34A]/50' : 'cursor-default'
    }`;

    const content = (
      <>
        <div className="flex items-center gap-4">
          <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" />
          <div>
            <div className="font-semibold text-white text-sm sm:text-base group-hover:text-[#16A34A] transition-colors">
              {project.country}
            </div>
            <div className="text-[10px] text-slate-500 font-medium uppercase tracking-[0.16em] mt-1">
              {project.status}
            </div>
          </div>
        </div>
        <ChevronRight
          size={18}
          className="text-slate-600 group-hover:text-[#16A34A] group-hover:translate-x-0.5 transition-all"
        />
      </>
    );

    if (!clickable) {
      return (
        <div key={key} className={className}>
          {content}
        </div>
      );
    }

    if (isExternalUrl(link)) {
      return (
        <a key={key} href={link} className={className} aria-label={ariaLabel}>
          {content}
        </a>
      );
    }

    return (
      <Link key={key} to={normalizeInternalPath(link)} className={className} aria-label={ariaLabel}>
        {content}
      </Link>
    );
  };

  const globeLocations = [
    {
      name: 'Siège Social',
      city: 'Douala',
      country: 'Cameroun',
      type: 'Bureau Principal',
      year: '2017',
      coordinates: [9.7085, 4.0511],
      description: "Siège social et centre opérationnel principal d'Africa Systems"
    },
    {
      name: 'Bureau Régional',
      city: 'Nairobi',
      country: 'Kenya',
      type: 'Filiale',
      year: '2021',
      coordinates: [36.8219, -1.2921],
      description: "Pôle d'excellence technologique en Afrique de l'Est"
    },
    {
      name: 'Bureau Régional',
      city: 'Lagos',
      country: 'Nigeria',
      type: 'Filiale',
      year: '2019',
      coordinates: [3.3792, 6.5244],
      description: "Hub stratégique pour l'Afrique de l'Ouest"
    },
    {
      name: 'Bureau Régional',
      city: 'Cotonou',
      country: 'Bénin',
      type: 'Filiale',
      year: '2020',
      coordinates: [2.3158, 6.3703],
      description: "Base d'opérations régionales"
    },
    {
      name: 'Bureau Régional',
      city: 'Johannesburg',
      country: 'Afrique du Sud',
      type: 'Filiale',
      year: '2024',
      coordinates: [28.0473, -26.2041],
      description: 'Expansion stratégique en Afrique Australe'
    }
  ];

  return (
    <section className="relative bg-[#0a1628] text-white overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#16A34A]/60 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 mb-12 lg:mb-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-[#16A34A]" />
              <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
                Afrique
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
              {title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-slate-400 leading-relaxed">{description}</p>
          </div>
        </div>

        {/* Carte + chiffres signature */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch mb-14 lg:mb-20">
          <div className="lg:col-span-8 relative overflow-hidden border border-white/5">
            <GlobeMap
              locations={globeLocations}
              className="!rounded-none !shadow-none !h-[420px] md:!h-[520px] lg:!h-[560px]"
            />
          </div>

          <div
            ref={statsRef}
            className="lg:col-span-4 flex flex-col justify-between gap-8 border border-white/10 p-8 lg:p-10"
          >
            <div>
              <div className="text-[4.5rem] md:text-[5.5rem] font-bold leading-none tracking-tight tabular-nums text-white">
                {statsInView ? (
                  <CountUp end={totalCountries} duration={2.2} formattingFn={(n) => String(n).padStart(2, '0')} />
                ) : (
                  '00'
                )}
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#16A34A] font-semibold">
                {intl.formatMessage({ id: 'countries.served' })}
              </div>
            </div>

            <div className="w-full h-px bg-white/10" />

            <div>
              <div className="text-[4.5rem] md:text-[5.5rem] font-bold leading-none tracking-tight tabular-nums text-white">
                {statsInView ? <CountUp end={totalEmployees} duration={2.2} prefix="+" /> : '+0'}
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#16A34A] font-semibold">
                {intl.formatMessage({ id: 'employees.continent' })}
              </div>
            </div>

            <p className="text-sm text-slate-500 leading-relaxed border-t border-white/10 pt-6">
              Notre technologie traverse les frontières.
            </p>
          </div>
        </div>

        {/* Drapeaux — bande éditoriale */}
        <div className="flex flex-wrap items-center gap-6 md:gap-10 mb-16 lg:mb-20 pb-10 border-b border-white/10">
          {countries.map((country, index) => (
            <div key={index} className="group flex items-center gap-3">
              <div className="w-10 h-10 overflow-hidden border border-white/15 group-hover:border-[#16A34A]/50 transition-colors">
                <CountryFlag
                  code={country.flag}
                  className="w-full h-full object-cover"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.16em] group-hover:text-white transition-colors">
                {country.name}
              </span>
            </div>
          ))}
        </div>

        {/* Autres projets */}
        {additionalProjects.length > 0 && (
          <div>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#16A34A] mb-3">
                  Expansion
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white">
                  {intl.formatMessage({ id: 'other.projects' })}
                </h3>
              </div>
              <p className="text-slate-500 text-sm md:text-base max-w-md">
                Présence active et projets stratégiques en cours de déploiement.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8">
              {additionalProjects.map(renderAdditionalProject)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GeographicPresenceSection;
